// Rewrites a service `valueExpression` renderer into one esri-leaflet-renderers can drive.
//
// esri-leaflet-renderers classifies features by a literal attribute field — uniqueValue reads
// `rendererJson.field1`, classBreaks reads `rendererJson.field` — and has no Arcade engine. This
// helper compiles the renderer's Arcade `valueExpression` (via arcade-mini) and rewrites the
// renderer to classify on a synthetic field instead; the caller stamps that field onto each
// feature with the returned `evaluate`. The original drawingInfo is left untouched.
//
// Returns:
//   null                                  -> no shimming needed (no renderer, or a literal field)
//   { supported: false }                  -> a valueExpression is present but cannot be shimmed
//                                            (uncompilable expression, or unsupported renderer type)
//   { supported: true, drawingInfo,        -> rewritten drawingInfo + the synthetic field name +
//     field, evaluate }                       an evaluator that produces that field's value

import { compileArcade } from "./arcade-mini.mjs";

export const SYNTHETIC_FIELD = "__gsArcadeValue";

export function prepareArcadeRenderer(drawingInfo) {
	const renderer = drawingInfo && drawingInfo.renderer;
	if (!renderer || !renderer.valueExpression) {
		return null;
	}

	const program = compileArcade(renderer.valueExpression);
	if (program === null) {
		return { supported: false };
	}

	const rewritten = Object.assign({}, renderer);
	delete rewritten.valueExpression;
	delete rewritten.valueExpressionTitle;

	if (renderer.type === "uniqueValue") {
		// esri-leaflet-renderers reads field1 (with optional field2/field3 + delimiter) for uniqueValue.
		rewritten.field1 = SYNTHETIC_FIELD;
		delete rewritten.field;
		delete rewritten.field2;
		delete rewritten.field3;
		delete rewritten.fieldDelimiter;
	} else if (renderer.type === "classBreaks") {
		// esri-leaflet-renderers reads field for classBreaks; normalization no longer applies once
		// the synthetic field already holds the final computed value.
		rewritten.field = SYNTHETIC_FIELD;
		delete rewritten.normalizationField;
		delete rewritten.normalizationType;
	} else {
		// Other renderer types (heatmap, dotDensity, predominance, …) that carry a valueExpression
		// are outside what esri-leaflet-renderers can classify; let the caller fall back + warn.
		return { supported: false };
	}

	return {
		supported: true,
		field: SYNTHETIC_FIELD,
		drawingInfo: Object.assign({}, drawingInfo, { renderer: rewritten }),
		evaluate(props) {
			return program.evaluate(props);
		},
	};
}
